import {
  DocPage,
  DocSection,
  K,
  OL,
  P,
  PageFooter,
  PromptCard,
} from "./_components";

export function Remixing() {
  return (
    <DocPage
      eyebrow="Collaborating"
      title="Remixing & forking"
      intro={
        <>
          Two ways to branch off existing work without touching the original: a
          one-shot remix that clones a whole project so you can re-theme or
          experiment, and a single-widget fork that lifts one widget into
          another project. Both re-bundle under the new namespace, so artifacts
          never collide and the source project is left intact.
        </>
      }
    >
      <DocSection id="remix" title="Remix a project to re-theme it">
        <P>
          One call clones every tool in a project into a new project — UI tools
          re-bundle under the new namespace (so S3 keys don't collide), data
          tools copy as-is. Public-share tokens and secrets don't propagate; the
          source project is untouched. Use this when you want to fork a project
          to apply a different brand, color scheme, or copy without losing the
          original.
        </P>
        <PromptCard
          title="Re-theme a whole project in one prompt"
          prompt={`Use project remix on my "Examples" project. New project name: "Examples (Dark)". Override icon: "🌙" and accentColor: "#7C3AED". When that's done, run remix_widget on each cloned UI widget to apply a darker theme — deep purple accents, near-black backgrounds, higher contrast type — keeping the existing logic and props intact. Show me the new project's MCP URL.`}
          note={
            <>
              You'll see a per-tool created/failed count — one bad widget never
              kills the batch.
            </>
          }
        />
      </DocSection>

      <DocSection id="fork-widget" title="Fork a single widget across projects">
        <P>
          If you don't want to clone a whole project, you can copy one widget
          into another project. The destination project re-bundles the widget
          under its own namespace, so the original is untouched.
        </P>
        <PromptCard
          title="Fork one widget to another project"
          prompt={`Use save_widget_to_app to copy the "quarterly-revenue" widget from my "Sales" project into my "Q4 Board Deck" project. Keep the tool name the same.`}
        />
        <P>
          Shared users can fork too — they need <K>read</K> permission only.
          The destination is their own project (or Inbox).
        </P>
      </DocSection>

      <DocSection id="which" title="Remix the project, or fork one widget?">
        <OL>
          <li>
            <strong>Branch a whole project to experiment</strong> → Remix. Edit
            the clone; the original keeps shipping.
          </li>
          <li>
            <strong>Lift one widget into a different project</strong> →
            save_widget_to_app. No new project, just a copy.
          </li>
        </OL>
      </DocSection>

      <PageFooter
        prev={{ to: "/docs/sharing", label: "Sharing & teams" }}
        next={{ to: "/docs/troubleshooting", label: "Troubleshooting" }}
      />
    </DocPage>
  );
}
